import { useLocation } from "react-router-dom";
import PageTitle from "../../components/common/page-title/page-title";
import { useEffect, useRef, useState } from "react";
import {
  SERVICE_CONSUMER_PROMO,
  SERVICE_EVENT_MARKETING,
  SERVICE_SPORT_MARKETING,
} from "../../const/const";
import iEventMarketing from "../../assets/icons/service-page/event-marketing-filled.svg";
import iSportMarketing from "../../assets/icons/service-page/sport-marketing-filled.svg";
import iConsumerPromo from "../../assets/icons/service-page/consumer-promo-filled.svg";
import { TCase, TDirectionFilter, TService } from "../../types/types";
import { EServiceName } from "../../types/enum";
import iX5Partner from "../../assets/icons/partners-block/x5-partner-logo.svg";
import iPresidentPartner from "../../assets/icons/partners-block/president-partner-logo.svg";
import iYandexGoPartner from "../../assets/icons/partners-block/yandex-go-partner-logo.svg";
import iBurgerKingPartner from "../../assets/icons/partners-block/burger-king-partner-logo.svg";
import iSamokatPartner from "../../assets/icons/partners-block/samokat-partner-logo.svg";
import iTBankPartner from "../../assets/icons/partners-block/tbank-partner-logo.svg";
import iABlnBevPartner from "../../assets/icons/partners-block/ablnbev-partner-logo.svg";
import iHealthPartner from "../../assets/icons/partners-block/health-partner-logo.svg";
import iAlfaBankPartner from "../../assets/icons/partners-block/alfa-bank-partner-logo.svg";
import iSbpPartner from "../../assets/icons/partners-block/sbp-partner-logo.svg";
import iFonbetPartner from "../../assets/icons/partners-block/fonbet-partner-logo.svg";
import iLentaPartner from "../../assets/icons/partners-block/lenta-partner-logo.svg";
import "./service-page.sass";
import PartnerItem from "../../components/common/partner-item/partner-item";
import {
  getCasesApi,
  getCasesByDirectionFilterApi,
  getDirectionsApi,
} from "../../api/api";
import Case from "../../components/common/cases-mock/case";
import Loading from "../../components/common/loading/loading";
import CasesMenu from "../../components/cases-page/cases-menu/cases-menu";
import iBluePolygon from "../../assets/icons/blue-polygon.svg";

const ServicePage = () => {
  const location = useLocation();
  const [service, setService] = useState<TService | null>(null);
  const serviceName = location.pathname.split("/").pop();
  const [hover, setHover] = useState<undefined | number>(undefined);
  const [cases, setCases] = useState<TCase[]>([]);
  const [isCasesLoading, setCasesLoading] = useState(true);
  const [directions, setDirections] = useState<TDirectionFilter[]>([]);
  const [isDirectionsLoading, setDirectionsLoading] = useState(true);
  const [selected, setSelected] = useState<
    number | { index: number; slug: string }[]
  >(0);
  const [casesError, setCasesError] = useState("");
  const [directionsError, setDirectionsError] = useState("");
  const [isCasesRefetching, setCasesRefetching] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const skipFirstSelectedSyncRef = useRef(true);

  const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === "AbortError";

  const partnersListConsumerPromo = [
    iX5Partner,
    iPresidentPartner,
    iYandexGoPartner,
    iBurgerKingPartner,
  ];
  const partnersListSportMarketing = [
    iSbpPartner,
    iAlfaBankPartner,
    iFonbetPartner,
    iLentaPartner,
  ];
  const partnersListEventMarketing = [
    iSamokatPartner,
    iTBankPartner,
    iABlnBevPartner,
    iHealthPartner,
  ];

  useEffect(() => {
    skipFirstSelectedSyncRef.current = true;
    setSelected(0);
    setCasesLoading(true);
    setDirectionsLoading(true);

    if (!serviceName) {
      setCases([]);
      setCasesLoading(false);
      setDirectionsLoading(false);
      return;
    }

    const casesController = new AbortController();
    const directionsController = new AbortController();

    getCasesApi({ signal: casesController.signal })
      .then((data) => {
        setCases(data.data);
        setCasesError("");
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setCasesError("Не удалось загрузить кейсы по услуге.");
      })
      .finally(() => {
        if (!casesController.signal.aborted) {
          setCasesLoading(false);
        }
      });

    getDirectionsApi({ signal: directionsController.signal })
      .then((data) => {
        setDirections(data.data);
        setDirectionsError("");
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setDirectionsError("Не удалось загрузить направления.");
      })
      .finally(() => {
        if (!directionsController.signal.aborted) {
          setDirectionsLoading(false);
        }
      });

    return () => {
      casesController.abort();
      directionsController.abort();
    };
  }, [serviceName, reloadKey]);

  useEffect(() => {
    if (serviceName) {
      switch (serviceName) {
        case SERVICE_EVENT_MARKETING.name:
          setService(SERVICE_EVENT_MARKETING);
          break;
        case SERVICE_SPORT_MARKETING.name:
          setService(SERVICE_SPORT_MARKETING);
          break;
        case SERVICE_CONSUMER_PROMO.name:
          setService(SERVICE_CONSUMER_PROMO);
          break;
        default:
          setService(SERVICE_CONSUMER_PROMO);
          break;
      }
    }
  }, [serviceName]);

  useEffect(() => {
    if (skipFirstSelectedSyncRef.current) {
      skipFirstSelectedSyncRef.current = false;
      return;
    }

    if (!service) {
      return;
    }

    const controller = new AbortController();
    setCasesRefetching(true);

    if (selected === 0) {
      getCasesApi({ signal: controller.signal })
        .then((data) => {
          setCases(data.data);
          setCasesError("");
        })
        .catch((error) => {
          if (isAbortError(error)) {
            return;
          }
          setCasesError("Не удалось загрузить кейсы.");
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setCasesRefetching(false);
          }
        });
    } else if (Array.isArray(selected)) {
      getCasesByDirectionFilterApi(
        selected.map((item) => item.slug).join(","),
        { signal: controller.signal },
      )
        .then((data) => {
          setCases(data.data);
          setCasesError("");
        })
        .catch((error) => {
          if (isAbortError(error)) {
            return;
          }
          setCasesError("Не удалось отфильтровать кейсы по направлению.");
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setCasesRefetching(false);
          }
        });
    } else {
      setCasesRefetching(false);
    }

    return () => {
      controller.abort();
    };
  }, [selected, service]);

  return (
    <div className="page service-page">
      {directionsError && <div className="error">{directionsError}</div>}
      {casesError && <div className="error">{casesError}</div>}
      {(casesError || directionsError) && (
        <button
          type="button"
          className="link"
          onClick={() => setReloadKey((prev) => prev + 1)}
        >
          Повторить загрузку
        </button>
      )}

      <div className="service-page__container">
        {service && (
          <img
            src={
              service.name === EServiceName.CONSUMER_PROMO
                ? iConsumerPromo
                : service.name === EServiceName.SPORT_MARKETING
                  ? iSportMarketing
                  : iEventMarketing
            }
            className="service-page__img"
            alt=""
          />
        )}

        <div className="service-page__content">
          {service && (
            <PageTitle className={`service-page__title_${service.name}`}>
              {service.title}
            </PageTitle>
          )}

          {service && (
            <div className="service-page__description">
              {service.description}
            </div>
          )}
        </div>
      </div>

      <div className="service-page__partners">
        <div className="service-page__partners_title">с нами работают</div>

        <div className="service-page__partners_list">
          {service && service.name === EServiceName.CONSUMER_PROMO
            ? partnersListConsumerPromo.map((partner, index) => (
                <PartnerItem
                  img={partner}
                  index={index + 1}
                  setHover={setHover}
                  hover={hover}
                  key={index + 1}
                />
              ))
            : service && service.name === EServiceName.SPORT_MARKETING
              ? partnersListSportMarketing.map((partner, index) => (
                  <PartnerItem
                    img={partner}
                    index={index + 1}
                    setHover={setHover}
                    hover={hover}
                    key={index + 1}
                  />
                ))
              : partnersListEventMarketing.map((partner, index) => (
                  <PartnerItem
                    img={partner}
                    index={index + 1}
                    setHover={setHover}
                    hover={hover}
                    key={index + 1}
                  />
                ))}
        </div>
      </div>

      <div className="service-page__cases">
        <div className="service-page__cases_menu">
          <Loading isLoading={isDirectionsLoading} />

          {!isDirectionsLoading && (
            <CasesMenu
              className="service-page__menu"
              flexDirection="column"
              menuItems={directions.map((direction) => ({
                id: direction.id,
                title: direction.title,
                slug: direction.slug,
                icon: iBluePolygon as string,
              }))}
              selected={selected}
              setSelected={setSelected}
            />
          )}
        </div>
        <div className="service-page__cases_list">
          <Loading isLoading={isCasesLoading || isCasesRefetching} />

          {!isCasesLoading &&
            !isCasesRefetching &&
            cases.map((caseData) => (
              <Case key={caseData.id} caseData={caseData} />
            ))}
          {!isCasesLoading &&
            !isCasesRefetching &&
            !casesError &&
            cases.length === 0 && <div className="error">Кейсы не найдены.</div>}
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
